import { fetchDashboardDataFromSheet } from "../../api/sheetsApi";
import { useEffect, useState } from "react";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import {
  Users,
  Briefcase,
  Truck,
  Building2,
  UserMinus,
  ClipboardList,
} from "lucide-react";
import OverviewHeader from "./OverviewHeader";

const campusColumns = [
  { key: "perumbakkam", label: "Perumbakkam", color: "#A21CAF" },
  { key: "kvr", label: "KVR", color: "#0EA5E9" },
  { key: "thiruvanmiyur", label: "Thiruvanmiyur", color: "#EF4444" },
  { key: "thirumudivakkam", label: "Thirumudivakkam", color: "#FACC15" },
  { key: "hyderbad", label: "Hyderabad", color: "#84CC16" },
];

const cardConfig = {
  "Teaching Staff": {
    title: "Teaching Staff",
    subtitle: "All Campuses",
    bg: "bg-pink-100",
    icon: <Users size={18} />,
    iconBg: "bg-pink-600",
  },
  Administration: {
    title: "Administration",
    subtitle: "Campus Admin",
    bg: "bg-sky-100",
    icon: <Building2 size={18} />,
    iconBg: "bg-sky-600",
  },
  Housekeeping: {
    title: "Housekeeping",
    subtitle: "Campus HK",
    bg: "bg-red-100",
    icon: <ClipboardList size={18} />,
    iconBg: "bg-red-500",
  },
  Drivers: {
    title: "Drivers",
    subtitle: "Transport",
    bg: "bg-yellow-100",
    icon: <Truck size={18} />,
    iconBg: "bg-yellow-500",
  },
  "Attrition Rate": {
    title: "Attrition Rate",
    subtitle: "Group Level",
    bg: "bg-lime-100",
    icon: <UserMinus size={18} />,
    iconBg: "bg-lime-600",
    suffix: "%",
  },
  "Open Vacancies": {
    title: "Open Vacancies",
    subtitle: "Actively Hiring",
    bg: "bg-cyan-100",
    icon: <Briefcase size={18} />,
    iconBg: "bg-cyan-600",
  },
};

const cardOrder = [
  "Teaching Staff",
  "Administration",
  "Housekeeping",
  "Drivers",
  "Attrition Rate",
  "Open Vacancies",
];

const metricAliases = {
  "Teaching Staff": ["Teaching Staff", "Total Teaching", "Teachers"],
  Administration: ["Administration", "Total Administration", "Admin"],
  Housekeeping: ["Housekeeping", "Total Housekeeping"],
  Drivers: ["Drivers", "Total Drivers"],
  "Attrition Rate": ["Attrition Rate", "Attrition"],
  "Open Vacancies": ["Open Vacancies", "Total Vacancies", "Vacancies"],
};

const staffColors = {
  "Teaching Staff": "#0EA5E9",
  Teachers: "#0EA5E9",
  Administration: "#EF4444",
  Admin: "#EF4444",
  Housekeeping: "#FACC15",
  Drivers: "#84CC16",
  Coaches: "#A855F7",
  Coach: "#A855F7",
  "Head Office": "#FB7185",
};

const getRowLabel = (row) =>
  row?.category || row?.metric || row?.name || row?.type || row?.staffType || "";

const getCampusValue = (row, campusKey) => {
  if (campusKey === "hyderbad") {
    return Number(row?.hyderbad ?? row?.hyderabad ?? 0);
  }

  return Number(row?.[campusKey] || 0);
};

const getCampusSum = (row) =>
  campusColumns.reduce((total, campus) => total + getCampusValue(row, campus.key), 0);

const getGroupTotal = (row) => {
  const groupTotal = Number(row?.groupTotal ?? row?.total ?? 0);
  return groupTotal > 0 ? groupTotal : getCampusSum(row);
};

const formatValue = (value, suffix = "") =>
  `${Number(value || 0).toLocaleString("en-IN")}${suffix}`;

const normalizeMetric = (value = "") =>
  value.toLowerCase().replace(/^total\s+/, "").trim();

const getMetricRow = (rows, metric) => {
  const aliases = metricAliases[metric] || [metric];
  const normalizedAliases = aliases.map(normalizeMetric);

  return rows.find((row) =>
    normalizedAliases.includes(normalizeMetric(getRowLabel(row)))
  );
};

const isVacancyRow = (row) => getRowLabel(row).toLowerCase().includes("vacanc");
const isAttritionRow = (row) => getRowLabel(row).toLowerCase().includes("attrition");
const isTotalRow = (row) => getRowLabel(row).toLowerCase().includes("total (");

const getCards = (rows) =>
  cardOrder.map((metric) => {
    const config = cardConfig[metric];
    const row = getMetricRow(rows, metric);

    return {
      ...config,
      value: formatValue(getGroupTotal(row), config.suffix),
    };
  });

const getStaffData = (rows) =>
  rows
    .filter((row) => !isVacancyRow(row) && !isAttritionRow(row) && !isTotalRow(row))
    .map((row) => {
      const name = getRowLabel(row);

      return {
        name: name.replace(/^Total\s+/, ""),
        value: getGroupTotal(row),
        color: staffColors[name] || staffColors[name.replace(/^Total\s+/, "")] || "#64748B",
      };
    })
    .filter((item) => item.name && item.value > 0);

const getVacancyData = (rows) => {
  const vacancyRow = rows.find(isVacancyRow);

  return campusColumns.map((campus) => ({
    campus: campus.label,
    value: getCampusValue(vacancyRow, campus.key),
    color: campus.color,
  }));
};

const getTableRows = (rows) => rows.filter((row) => !isAttritionRow(row));

export default function HumanResourcesDashboard() {
  const [hrData, setHrData] = useState([]);
  const [hrGroupTotals, setHrGroupTotals] = useState({});
  const [currentMonth, setCurrentMonth] = useState("May 2026");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchHrData = async () => {
      try {
        const data = await fetchDashboardDataFromSheet();
        setHrData(data?.hrRows || []);
        setHrGroupTotals(data?.hrGroupTotals || {});
        setCurrentMonth(data?.currentMonth || "May 2026");
      } catch (err) {
        console.error(err);
        setError(err.message || "Unable to load HR data. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchHrData();
  }, []);

  if (loading) {
    return (
      <div className="p-2 bg-gray-100 min-h-[100dvh] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-[100dvh] items-center justify-center bg-gray-100 p-2">
        <div className="rounded-xl bg-white px-5 py-4 text-sm font-medium text-red-500 shadow-sm">
          <p>{error}</p>
         
        </div>
      </div>
    );
  }

  const cards = getCards(hrData);
  const staffData = getStaffData(hrData);
  const vacancyData = getVacancyData(hrData);
  const tableRows = getTableRows(hrData);
  const cardstatic = [
    {
      title: "Total COAE",
      value: formatValue(hrGroupTotals["total coae"] || 0),
      subtitle: "COAE Staff",
      bg: "bg-purple-50",
      iconBg: "bg-purple-500",
      icon: <Briefcase />,
    },
    {
      title: "Total Head Office",
      value: formatValue(hrGroupTotals["total head office"] || 0),
      subtitle: "Head Office Staff",
      bg: "bg-orange-50",
      iconBg: "bg-orange-500",
      icon: <Building2 />,
    },
  ];
  const dashboardCards = [...cards, ...cardstatic];

  return (
    <div className="p-2 bg-gray-100 min-h-[100dvh] space-y-6">
      <OverviewHeader currentMonth={currentMonth} />

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        {dashboardCards.map((card) => (
          <div
            key={card.title}
            className={`${card.bg} rounded-2xl p-4 shadow-sm`}
          >
            <div
              className={`mb-2 flex h-8 w-8 items-center justify-center rounded-xl text-white ${card.iconBg}`}
            >
              {card.icon}
            </div>

            <p className="text-sm text-gray-600">{card.title}</p>

            <h3 className="mt-1 text-2xl font-bold text-gray-800">
              {card.value}
            </h3>

            <p className="mt-2 text-xs text-gray-500">{card.subtitle}</p>
          </div>
        ))}
      </div>

      <div className="mt-5 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="rounded-2xl bg-white p-4 shadow-sm">
          <h3 className="mb-5 text-sm font-semibold text-gray-700">
            Staff Composition (Group)
          </h3>

          <div className="flex flex-col items-center gap-6 lg:flex-row">
            <div className="h-52 w-52 relative">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={staffData}
                    dataKey="value"
                    innerRadius={45}
                    outerRadius={65}
                    paddingAngle={4}
                    isAnimationActive={false}
                  >
                    {staffData.map((entry) => (
                      <Cell key={entry.name} fill={entry.color} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <span className="text-xl font-bold text-gray-800">
                  {formatValue(staffData.reduce((sum, item) => sum + (item.value || 0), 0))}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {staffData.map((item) => (
                <div key={item.name} className="flex items-center gap-2">
                  <div
                    className="h-3 w-3 rounded-full"
                    style={{ backgroundColor: item.color }}
                  />
                  <span className="text-sm text-gray-600">
                    {item.name} ({formatValue(item.value)})
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="rounded-2xl bg-white p-4 shadow-sm">
          <h3 className="mb-5 text-sm font-semibold text-gray-700">
            Campus-wise Vacancies
          </h3>

          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={vacancyData}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="campus" tick={{ fontSize: 11 }} />
                <YAxis />
                <Tooltip />

                <Bar dataKey="value" radius={[8, 8, 0, 0]} isAnimationActive={false}>
                  {vacancyData.map((entry) => (
                    <Cell key={entry.campus} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="mt-5 overflow-hidden rounded-2xl bg-white shadow-sm">
        <div className="border-b px-5 py-4">
          <h3 className="text-sm font-semibold text-gray-700">
            Campus-wise HR Breakdown
          </h3>
        </div>

        <div className="overflow-auto">
          <table className="w-full min-w-[900px] border-collapse">
            <thead className="bg-gray-50/50 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider border-b">
              <tr>
                <th className="px-6 py-4">Category</th>
                {campusColumns.map((campus) => (
                  <th key={campus.key} className="px-6 py-4">
                    {campus.label}
                  </th>
                ))}
                <th className="px-6 py-4 text-right">Group Total</th>
              </tr>
            </thead>

            <tbody className="text-sm">
              {tableRows.map((row) => (
                <tr
                  key={getRowLabel(row)}
                  className="border-b border-gray-100 text-gray-700 transition-colors hover:bg-blue-50/50"
                >
                  <td className="px-6 py-4 font-medium">{getRowLabel(row)}</td>

                  {campusColumns.map((campus) => (
                    <td key={campus.key} className="px-6 py-4">
                      {formatValue(getCampusValue(row, campus.key))}
                    </td>
                  ))}

                  <td className="px-6 py-4 font-semibold text-right">
                    {formatValue(getGroupTotal(row))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
